// Personbinding: HVEM tilhører HVILKEN ORBAT-node — og er det verifisert.
//
// Dette er registeret som manglet (kartlagt 2026-08-13): ORBAT-modellen fantes,
// rolleutledningen fantes, men relasjonen person→node ble aldri lagret. «Send
// nøkkel»-flyten kastet personens Matrix-id rett etter utsendelse, og
// tilhørighet levde som FRITEKST i brukerens egne private kontodata i GNÅ
// (`no.veifo.gna.profile`) — selvrapportert, uleselig for andre, og dermed
// ubrukelig som tilgangsstyring.
//
// Prinsipper (eierbesluttet 2026-08-13, jf. vale/FELTVEKSLER_ADMIN.md):
//   1. ORBAT er sannheten, ikke fritekst. Bindingen peker på en node-ID.
//   2. Verifisering gjøres av OVERORDNET I DIREKTE LINJE — ikke av en generisk
//      admin-rolle. Regelen håndheves i `@skade/rbac` (`kanVerifisere`), som
//      bruker `orbatErOverordnet` fra orbat.ts.
//   3. Registrering skal IKKE begrenses til en personell-liste. Identiteten
//      kommer fra `identitetskilde` — i dag telefon/OTP, senere Forsvarets
//      «min side». Derfor er kilden et FELT, ikke en antakelse.
//
// Modulen er REN og deterministisk (event sourcing, samme mønster som orbat.ts)
// slik at hele livsløpet kan enhetstestes uten Matrix, database eller nettverk.
import { z } from 'zod';
/** Hvor identiteten er bekreftet fra. Utvides når nye kilder kobles på —
 * `forsvaret-minside` gir personbekreftelse (at dette ER personen), men ikke
 * ORBAT-tilhørighet: den settes fortsatt av overordnet. */
export const Identitetskilde = z.enum([
    /** Selvbetjent SMS-registrering (infra/otp-service.py). Bekrefter kun at
     * innehaveren rår over telefonnummeret — ikke hvem personen er. */
    'otp-telefon',
    /** Forsvarets «min side» (planlagt). Bekrefter personen. */
    'forsvaret-minside',
    /** Lagt inn av administrator/personelluttrekk (infra/provision-users.py). */
    'manuell',
]);
export const Kontostatus = z.enum(['aktiv', 'stengt']);
/**
 * Én persons binding til ORBAT. Nøkkelen er GNÅ-Matrix-id-en, fordi den er den
 * ene identiteten som finnes FØR personen har noen SKADE-konto — og fordi den
 * er deterministisk utledet av telefonnummeret (`gna<E.164>`).
 */
export const PersonBindingSchema = z.object({
    /** «@gna4791994407:matrix.follodefence.com». Primærnøkkel. */
    gnaMatrixId: z.string().min(3).max(255),
    /** SKADE-konto-id når personen har løst inn en enrolment-nøkkel. Utledes av
     * bindingen — brukeren skal ikke lenger kunne finne på sin egen (hullet
     * kartlagt 2026-08-13: koden var en ren bærer-hemmelighet uten adressat). */
    skadeUserId: z.string().max(64).optional(),
    /** ORBAT-node personen tilhører (`OrbatUnit.id`). */
    orbatNodeId: z.string().max(64),
    /**
     * RBAC-org-scope UTLEDET av nodens slektslinje (`orgFraOrbatLinje` i
     * @skade/rbac) idet bindingen ble satt.
     *
     * Hvorfor den lagres og ikke regnes ut ved behov: ORBAT-treet ligger i dag
     * bare i nettleserens localStorage (P0-2/QA-002), så hubben KAN ikke slå opp
     * slektslinja selv. Uten dette feltet ville personposisjoner blitt stående
     * org-løse — og org-løse ressurser er lesbare for alle roller fra menig og
     * opp. MERK: endres treet, må bindingene beregnes på nytt; det er prisen for
     * å ha en utledet verdi lagret, og den forsvinner når ORBAT flyttes
     * serverside.
     */
    org: z
        .object({
        district: z.string().max(64).optional(),
        area: z.string().max(64).optional(),
        troop: z.string().max(64).optional(),
        team: z.string().max(64).optional(),
    })
        .optional(),
    /** RBAC-rollenavn. Valideres mot rollelista i `@skade/rbac` (som importerer
     * contracts — derfor streng her, ikke enum: unngår sirkulær avhengighet). */
    rolle: z.string().max(32),
    /** Navn og grad settes av overordnet, ikke av brukeren selv. */
    navn: z.string().max(120).optional(),
    grad: z.string().max(40).optional(),
    identitetskilde: Identitetskilde.default('otp-telefon'),
    /** FALSK til en overordnet i direkte linje har bekreftet tilhørigheten. */
    verifisert: z.boolean().default(false),
    /** Hvem som verifiserte (GNÅ-Matrix-id til den overordnede) + når (ms epoch). */
    verifisertAv: z.string().max(255).optional(),
    verifisertMs: z.number().int().nonnegative().optional(),
    kontostatus: Kontostatus.default('aktiv'),
    opprettetMs: z.number().int().nonnegative().default(0),
});
/** Én medlemskapshendelse (event sourcing — `reduceMedlemskap` bygger tilstanden). */
export const MedlemskapEventSchema = z.discriminatedUnion('op', [
    /** Bind en person til en node (idempotent upsert). */
    z.object({ op: z.literal('tilordne'), binding: PersonBindingSchema }),
    /** Flytt personen til en annen node. NULLSTILLER verifiseringen med vilje:
     * den gamle bekreftelsen kom fra en annen kommandolinje og sier ingenting om
     * den nye. Ellers kunne en flytting brukes til å smugle et verifisert-merke
     * inn i en enhet ingen der har godkjent. */
    z.object({ op: z.literal('flytt'), gnaMatrixId: z.string().max(255), orbatNodeId: z.string().max(64) }),
    /** Sett navn/grad/rolle/identitetskilde/SKADE-konto (admin-overstyring). */
    z.object({
        op: z.literal('oppdater'),
        gnaMatrixId: z.string().max(255),
        patch: z.object({
            rolle: z.string().max(32).optional(),
            navn: z.string().max(120).optional(),
            grad: z.string().max(40).optional(),
            skadeUserId: z.string().max(64).optional(),
            identitetskilde: Identitetskilde.optional(),
        }),
    }),
    /** Overordnet i direkte linje bekrefter tilhørigheten. Myndigheten sjekkes
     * FØR hendelsen skrives (`kanVerifisere` i @skade/rbac) — reduseren stoler
     * på hendelsesstrømmen, slik `applyOrbatEvent` stoler på sin. */
    z.object({
        op: z.literal('verifiser'),
        gnaMatrixId: z.string().max(255),
        verifisertAv: z.string().max(255),
        ms: z.number().int().nonnegative(),
    }),
    /** Trekk tilbake en verifisering (feilført, personen har sluttet i enheten). */
    z.object({ op: z.literal('tilbakekall'), gnaMatrixId: z.string().max(255) }),
    /** Steng/gjenåpne kontoen (uten å slette bindingen — historikken består). */
    z.object({ op: z.literal('kontostatus'), gnaMatrixId: z.string().max(255), status: Kontostatus }),
    /** Fjern bindingen helt. */
    z.object({ op: z.literal('fjern'), gnaMatrixId: z.string().max(255) }),
]);
/** Bruk én hendelse på en bindingsliste. Ren (returnerer ny liste). */
export function applyMedlemskapEvent(bindinger, ev) {
    switch (ev.op) {
        case 'tilordne': {
            const finnes = bindinger.some((b) => b.gnaMatrixId === ev.binding.gnaMatrixId);
            if (!finnes)
                return [...bindinger, ev.binding];
            // Idempotent upsert, men en ny tilordning til en ANNEN node er en
            // flytting og skal derfor nullstille verifiseringen (samme grunn som
            // «flytt»). Tilordning til samme node beholder den.
            return bindinger.map((b) => {
                if (b.gnaMatrixId !== ev.binding.gnaMatrixId)
                    return b;
                const byttetNode = b.orbatNodeId !== ev.binding.orbatNodeId;
                return byttetNode ? nullstillVerifisering(ev.binding) : ev.binding;
            });
        }
        case 'flytt':
            return bindinger.map((b) => b.gnaMatrixId === ev.gnaMatrixId
                ? nullstillVerifisering({ ...b, orbatNodeId: ev.orbatNodeId })
                : b);
        case 'oppdater':
            return bindinger.map((b) => b.gnaMatrixId === ev.gnaMatrixId ? { ...b, ...tomtFjernet(ev.patch) } : b);
        case 'verifiser':
            return bindinger.map((b) => b.gnaMatrixId === ev.gnaMatrixId
                ? { ...b, verifisert: true, verifisertAv: ev.verifisertAv, verifisertMs: ev.ms }
                : b);
        case 'tilbakekall':
            return bindinger.map((b) => b.gnaMatrixId === ev.gnaMatrixId ? nullstillVerifisering(b) : b);
        case 'kontostatus':
            return bindinger.map((b) => b.gnaMatrixId === ev.gnaMatrixId ? { ...b, kontostatus: ev.status } : b);
        case 'fjern':
            return bindinger.filter((b) => b.gnaMatrixId !== ev.gnaMatrixId);
    }
}
/** Fjerner verifiseringen OG sporet av hvem som ga den (ikke bare flagget —
 * et etterlatt `verifisertAv` ville lest som om noen hadde godkjent). */
function nullstillVerifisering(b) {
    const { verifisertAv: _av, verifisertMs: _ms, ...rest } = b;
    return { ...rest, verifisert: false };
}
/** Dropper undefined-felter så en patch ikke blanker ut eksisterende verdier. */
function tomtFjernet(patch) {
    return Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined));
}
/** Bygg full medlemskapstilstand fra en hendelsesstrøm. Ren + testbar. */
export function reduceMedlemskap(events) {
    return events.reduce((acc, ev) => applyMedlemskapEvent(acc, ev), []);
}
/** Oppslag: bindingen for en GNÅ-Matrix-id (null om ukjent). Dette er
 * reverse-oppslaget broen mangler i dag — org-scope utledes nå av PERSONEN,
 * ikke av hvilket rom meldingen tilfeldigvis sto i (`ROOM_ORG_JSON`). */
export function bindingFor(bindinger, gnaMatrixId) {
    return bindinger.find((b) => b.gnaMatrixId === gnaMatrixId) ?? null;
}
/** Alle personer bundet til en gitt node (mannskapslista som ikke fantes). */
export function medlemmerAv(bindinger, orbatNodeId) {
    return bindinger.filter((b) => b.orbatNodeId === orbatNodeId);
}
/** Er personen bruksklar: bundet, verifisert av overordnet og ikke stengt.
 * Alt som gir tilgang skal spørre om DETTE — ikke bare om bindingen finnes. */
export function erOperativ(b) {
    return b !== null && b.verifisert && b.kontostatus === 'aktiv';
}
// ── Fra telefonnummer til et navngitt spor på kartet ────────────────────────
/**
 * Spor-id-en en GNÅ-brukers posisjon får i hubben.
 *
 * GNÅ sender `uid` = localpart av sin egen Matrix-id (`map_screen.dart`), og
 * CoT-inntaket prefikser med «cot:». Denne funksjonen er den ENE definisjonen
 * av den sammenhengen, slik at hubben kan slå en posisjon sammen med den rette
 * personbindingen — og slik at «har denne personen en posisjon?» kan besvares
 * for HVER registrert bruker, ikke bare for dem som rapporterer nå.
 */
export function personSporId(gnaMatrixId) {
    const localpart = gnaMatrixId.trim().replace(/^@/, '').replace(/:.*$/, '').toLowerCase();
    return localpart ? `cot:${localpart}` : '';
}
/** Etiketten en person skal vises med: «Grad Navn» når vi har begge, ellers det
 * beste vi har. Aldri tom — et navnløst spor på kartet er ubrukelig. */
export function personEtikett(b, reserve = '') {
    const navn = (b.navn ?? '').trim();
    const grad = (b.grad ?? '').trim();
    if (navn)
        return grad ? `${grad} ${navn}` : navn;
    const r = reserve.trim();
    if (r)
        return r;
    return b.gnaMatrixId.replace(/^@/, '').replace(/:.*$/, '');
}
/**
 * Gjør et rått BFT-spor om til et PERSON-spor ved hjelp av ORBAT-bindingen.
 *
 * Dette er sømmen som gjør et anonymt vennlig bakkesymbol merket
 * «GNA4791994407» til «Sersjant Ola Nordmann» med riktig org-scope. Ren
 * funksjon: hubben leverer sporet og bindingen, her skjer sammenstillingen.
 *
 * Uten binding beholdes sporet som det er — men det merkes likevel som person
 * med Matrix-id-en, for ellers ville en uregistrert bruker vært umulig å skille
 * fra et TAK-importert kjøretøy. `reserveNavn` er navnet fra
 * Synapse-katalogen (visningsnavnet brukeren selv har satt); det brukes bare
 * når bindingen ikke har et navn satt av overordnet, siden ORBAT skal vinne
 * over det selvrapporterte.
 */
export function berikPersonspor(spor, params) {
    const { gnaMatrixId, binding, reserveNavn } = params;
    if (!binding) {
        return {
            ...spor,
            kind: 'person',
            label: (reserveNavn ?? '').trim() || spor.label,
            person: { matrixId: gnaMatrixId, navn: (reserveNavn ?? '').trim() || undefined },
        };
    }
    return {
        ...spor,
        kind: 'person',
        label: personEtikett(binding, reserveNavn ?? spor.label),
        org: binding.org ?? spor.org,
        person: {
            matrixId: binding.gnaMatrixId,
            navn: binding.navn ?? ((reserveNavn ?? '').trim() || undefined),
            grad: binding.grad,
            orbatNodeId: binding.orbatNodeId,
            verifisert: binding.verifisert,
        },
    };
}
/** Er dette et gyldig jordisk koordinat? */
function gyldigPosisjon(lat, lon) {
    return (typeof lat === 'number' && Number.isFinite(lat) && lat >= -90 && lat <= 90 &&
        typeof lon === 'number' && Number.isFinite(lon) && lon >= -180 && lon <= 180);
}
/**
 * Tolker en GNÅ-geotag ut av en meldingstekst. Returnerer null når meldingen
 * ikke er en geotag — da skal den behandles som før (chat/rapport/osv.).
 */
export function parseGnaGeotag(body) {
    if (!body || !body.includes('gna_geotag'))
        return null;
    // JSON-linja kan ligge hvor som helst; vi tar den FØRSTE linja som parser og
    // bærer merket. Da tåler vi at overskriften endres uten at dette knekker.
    for (const linje of body.split('\n')) {
        const t = linje.trim();
        if (!t.startsWith('{') || !t.includes('gna_geotag'))
            continue;
        let o;
        try {
            o = JSON.parse(t);
        }
        catch {
            continue;
        }
        if (!o || o.gna_geotag === undefined)
            continue;
        if (!gyldigPosisjon(o.lat, o.lon))
            return null; // merket, men ubrukelig
        const tid = typeof o.time === 'string' ? Date.parse(o.time) : NaN;
        return {
            lat: o.lat,
            lon: o.lon,
            mgrs: typeof o.mgrs === 'string' && o.mgrs ? o.mgrs : undefined,
            tsMs: Number.isFinite(tid) ? tid : 0,
            accuracyM: typeof o.accuracy_m === 'number' && Number.isFinite(o.accuracy_m)
                ? o.accuracy_m
                : undefined,
            headingDeg: typeof o.heading === 'number' && Number.isFinite(o.heading)
                ? o.heading
                : undefined,
        };
    }
    return null;
}
/**
 * Geotag + avsender → PERSON-spor for kartet.
 *
 * `cepM` settes fra GPS-nøyaktigheten, så et punkt tatt med 124 meters
 * usikkerhet TEGNES med den usikkerheten i stedet for å se ut som en presis
 * observasjon. Etiketten er foreløpig avsenderens localpart; hubben beriker den
 * med navn og grad fra ORBAT-bindingen via `berikPersonspor`.
 */
export function geotagTilSpor(params) {
    const { geotag, senderId } = params;
    return {
        id: personSporId(senderId) || `geotag:${senderId}`,
        kind: 'person',
        affiliation: 'friend',
        lat: geotag.lat,
        lon: geotag.lon,
        sidc: 'SFGP------'.padEnd(15, '-'),
        label: senderId.replace(/^@/, '').replace(/:.*$/, ''),
        tsMs: geotag.tsMs || params.naaMs,
        cepM: geotag.accuracyM,
        source: 'gna-geotag',
        person: { matrixId: senderId },
    };
}
//# sourceMappingURL=medlemskap.js.map